import React from "react";
import { parseHTML } from "utils/htmlParser";

import styles from "./SectionHeader.module.scss";

/**
 * Section header component that displays title, description
 * 
 * @param {Object} props - Component properties
 * @param {string} props.title - Main title of the section
 * @param {string} props.short_desc - Short description shown as lead text
 * @param {string} props.desc - Main description text (can contain HTML)
 * @param {string} props.alignment - Text alignment (start, center, end)
 * @param {boolean} props.isFirst - Determines if this is the first section (uses h1 instead of h2)
 * @param {string} props.titleClasses - Additional CSS classes for the title
 * @param {Object} props.data - Optional data object containing title/desc/short_desc
 */
const SectionHeader = ({
	title,
	short_desc,
	desc,
	alignment,
	isFirst,
	titleClasses,
	data
}) => {
	const alignmentClass = alignment ? `text-${alignment}` : "";
	const TitleTag = isFirst ? "h1" : "h2";

	const renderTitle = data?.title || title || null;
	const renderShortDesc = data?.short_desc || short_desc || null;
	const renderDesc = data?.desc ? parseHTML(data?.desc) : desc ? parseHTML(desc) : null;
	
	const shouldRender = renderTitle || renderShortDesc || renderDesc;
	if (!shouldRender) return null;
	
	return (
		<header 
			className={`${styles.SectionHeader ?? ""}`}
			role="banner">
			{renderTitle && (
				<TitleTag 
					className={`title contrast ${alignmentClass} ${titleClasses || ""}`}
					id={`section-title-${renderTitle?.toLowerCase().replace(/\s+/g, '-')}`}>
					{renderTitle}
				</TitleTag>
			)}
			
			{renderShortDesc && (
				<p 
					className={`lead contrast ${alignmentClass}`}
					id={renderTitle ? `desc-${renderTitle?.toLowerCase().replace(/\s+/g, '-')}` : undefined}>
					{renderShortDesc}
				</p>
			)}
			
			{renderDesc && (
				<div 
					className={`desc contrast ${alignmentClass}`} 
					dangerouslySetInnerHTML={renderDesc}
					aria-labelledby={renderTitle ? `section-title-${renderTitle?.toLowerCase().replace(/\s+/g, '-')}` : undefined}>
				</div>
			)}
		</header>
	);
};

export default SectionHeader;
